import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { CalendarIcon, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTRPC } from "@/trpc";
import DatePicker from "@/components/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SaveButton } from "@/components/Button/SaveButton";
import type { SaveCredentialFieldInputValue } from "@/features/credential-fields/types/cedential-field";

const admittingPrivilegesSchema = z.object({
  facility_name: z.string().min(1, "Facility name is required").max(255),
  country: z.string().min(1, "Country is required").max(255),
  state: z.string().min(1, "State is required").max(2),
  city: z.string().min(1, "City is required").max(255),
  privilege_status: z.enum(["Active", "Pending", "Suspended", "Terminated"]),
  privilege_type: z.string().max(255).optional(),
  department: z.string().min(1, "Department is required").max(255),
  start_date: z.date(),
  end_date: z.date().optional(),
});

const formSchema = z.object({
  admittingPrivileges: z.array(admittingPrivilegesSchema),
});

type FormValues = z.infer<typeof formSchema>;
type AdmittingPrivilegeValue = Omit<
  Partial<z.infer<typeof admittingPrivilegesSchema>>,
  "start_date" | "end_date"
> & {
  start_date?: string | Date;
  end_date?: string | Date | null;
};
type CredentialFieldEntry<TValue = unknown> = {
  index: number;
  value: TValue | null;
};

export function AdmittingPrivilegesForm({
  onSave,
  admittingPrivileges,
  userId,
}: {
  onSave: () => void;
  admittingPrivileges: CredentialFieldEntry[];
  userId: string;
}) {
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      admittingPrivileges: admittingPrivileges
        .filter((val) => val.value)
        .map((val) => {
          const value = val.value as AdmittingPrivilegeValue;
          return {
            facility_name: value.facility_name || "",
            country: value.country || "",
            state: value.state || "",
            city: value.city || "",
            privilege_status: value.privilege_status || "Active",
            privilege_type: value.privilege_type || "",
            department: value.department || "",
            start_date: value.start_date
              ? new Date(value.start_date)
              : new Date(),
            end_date: value.end_date ? new Date(value.end_date) : undefined,
          };
        }),
    },
  });

  const mutation = useMutation(
    trpc.credentialFiles.saveCredentialsFields.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.credentialFiles.getCredentialsFields.queryKey(),
        });
        setSaveStatus("idle");
        setErrorMessage(null);
        onSave();
      },
      onError: (error) => {
        setSaveStatus("error");
        setErrorMessage(
          error instanceof Error ? error.message : "Failed to save changes",
        );
        setTimeout(() => setSaveStatus("idle"), 4000);
      },
    }),
  );

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "admittingPrivileges",
  });

  function onSubmit(data: FormValues) {
    setSaveStatus("saving");
    setErrorMessage(null);

    const existingIndices =
      admittingPrivileges.filter((val) => val.value).map((val) => val.index) ||
      [];
    const newIndexMax = data.admittingPrivileges.length;
    const indicesToDelete = existingIndices.filter(
      (index: number) => index > newIndexMax,
    );

    const fieldValues: SaveCredentialFieldInputValue[] = [
      ...data.admittingPrivileges.map((privilege, index) => ({
        fieldCode: "admitting_privileges",
        value: {
          facility_name: privilege.facility_name,
          country: privilege.country,
          state: privilege.state,
          city: privilege.city,
          privilege_status: privilege.privilege_status,
          privilege_type: privilege.privilege_type,
          department: privilege.department,
          start_date: format(privilege.start_date, "yyyy-MM-dd"),
          end_date: privilege.end_date
            ? format(privilege.end_date, "yyyy-MM-dd")
            : null,
        },
        index: index + 1,
        delete: false,
      })),
      ...indicesToDelete.map((index: number) => ({
        fieldCode: "admitting_privileges",
        value: null,
        index,
        delete: true,
      })),
    ];

    mutation.mutate({
      fieldValues,
      switchUserId: userId,
    });
  }

  return (
    <Form {...form}>
      {errorMessage && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-6 w-6" />
          <AlertDescription className="ml-2">{errorMessage}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="space-y-4 p-4 border rounded-lg relative"
            >
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="absolute right-2 top-2"
                onClick={() => remove(index)}
              >
                Remove
              </Button>

              <FormField
                control={form.control}
                name={`admittingPrivileges.${index}.facility_name`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Facility Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name={`admittingPrivileges.${index}.country`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`admittingPrivileges.${index}.state`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <Input {...field} maxLength={2} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`admittingPrivileges.${index}.city`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name={`admittingPrivileges.${index}.privilege_status`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Privilege Status</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Suspended">Suspended</SelectItem>
                        <SelectItem value="Terminated">Terminated</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`admittingPrivileges.${index}.privilege_type`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Privilege Type</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`admittingPrivileges.${index}.department`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Department</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name={`admittingPrivileges.${index}.start_date`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date</FormLabel>
                      <div className="relative">
                        <DatePicker
                          selected={field.value}
                          onChange={(date) => field.onChange(date)}
                          customInput={
                            <Input
                              value={
                                field.value
                                  ? format(field.value, "MM-dd-yyyy")
                                  : ""
                              }
                            />
                          }
                          dateFormat="MM-dd-yyyy"
                          wrapperClassName="w-full"
                        />
                        <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`admittingPrivileges.${index}.end_date`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date</FormLabel>
                      <div className="relative">
                        <DatePicker
                          selected={field.value}
                          onChange={(date) => field.onChange(date)}
                          customInput={
                            <Input
                              value={
                                field.value
                                  ? format(field.value, "MM-dd-yyyy")
                                  : ""
                              }
                            />
                          }
                          dateFormat="MM-dd-yyyy"
                          wrapperClassName="w-full"
                        />
                        <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 pointer-events-none" />
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({
                facility_name: "",
                country: "",
                state: "",
                city: "",
                privilege_status: "Active",
                privilege_type: "",
                department: "",
                start_date: new Date(),
                end_date: undefined,
              })
            }
          >
            Add Admitting Privilege
          </Button>
        </div>

        <SaveButton saveStatus={saveStatus} />
        <Button
          className="w-full py-6"
          type="button"
          variant="outline"
          onClick={() => onSave()}
        >
          Cancel
        </Button>
      </form>
    </Form>
  );
}
